import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { Application, ApplicationStatus, PricingPlan } from '../../types';
import { ToastContainer } from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorBanner } from '../../components/ErrorBanner';
import { useErrorBanner } from '../../hooks/useErrorBanner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending_approval: 'Pending Approval',
  pending_payment: 'Pending Payment',
  approved: 'Approved',
  payment_received: 'Payment Received',
  rejected: 'Rejected',
  converted: 'Converted',
  expired: 'Expired',
};

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  pending_approval: 'bg-yellow-100 text-yellow-800',
  pending_payment: 'bg-orange-100 text-orange-800',
  approved: 'bg-green-100 text-green-800',
  payment_received: 'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-800',
  converted: 'bg-purple-100 text-purple-800',
  expired: 'bg-gray-100 text-gray-800',
};

const VALID_TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  pending_approval: ['approved', 'rejected'],
  pending_payment: ['payment_received', 'expired'],
  approved: ['rejected'],
  payment_received: [],
  rejected: [],
  converted: [],
  expired: [],
};

export const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<PricingPlan[]>([]);

  // Status transition
  const [transitionTarget, setTransitionTarget] = useState<ApplicationStatus | null>(null);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Notes
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Convert
  const [showConvert, setShowConvert] = useState(false);
  const [convertPlanId, setConvertPlanId] = useState('');
  const [convertBillingMethod, setConvertBillingMethod] = useState<'automated' | 'invoice'>(
    'automated'
  );
  const [converting, setConverting] = useState(false);

  const { toasts, removeToast, showSuccess, showError } = useToast();
  const { error, showError: showBannerError, clearError } = useErrorBanner();

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [app, plansData] = await Promise.all([api.getApplication(id), api.getAllPlans()]);
      setApplication(app);
      setPlans(plansData);
    } catch (err) {
      showBannerError(err instanceof Error ? err.message : 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id, showBannerError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusTransition = async () => {
    if (!application || !transitionTarget) return;
    setTransitionLoading(true);
    try {
      const updated = await api.updateApplicationStatus(application.id, {
        status: transitionTarget,
        rejectionReason: transitionTarget === 'rejected' ? rejectionReason : undefined,
      });
      setApplication(updated);
      showSuccess(
        'Status Updated',
        `Application status changed to ${STATUS_LABELS[transitionTarget]}`
      );
      setTransitionTarget(null);
      setRejectionReason('');
    } catch (err) {
      showError('Update Failed', err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setTransitionLoading(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application || !newNote.trim()) return;
    setAddingNote(true);
    try {
      const updated = await api.addApplicationNote(application.id, newNote.trim());
      setApplication(updated);
      setNewNote('');
      showSuccess('Note Added', 'Admin note added successfully');
    } catch (err) {
      showError('Failed', err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleConvert = async () => {
    if (!application || !convertPlanId) return;
    setConverting(true);
    try {
      const { application: updated } = await api.convertApplication(application.id, {
        planId: convertPlanId,
        billingMethod: convertBillingMethod,
      });
      setApplication(updated);
      setShowConvert(false);
      showSuccess('Converted', 'Application converted to organisation successfully');
    } catch (err) {
      showError('Conversion Failed', err instanceof Error ? err.message : 'Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="text-center py-12">
        <ErrorBanner error={error || 'Application not found'} onClose={clearError} />
        <button
          onClick={() => navigate('/signup-queue')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/80"
        >
          Back to Queue
        </button>
      </div>
    );
  }

  const allowedTransitions = VALID_TRANSITIONS[application.status];
  const canConvert =
    application.formType !== 'free' &&
    (application.status === 'approved' || application.status === 'payment_received') &&
    !application.convertedOrganisationId;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/signup-queue')}
              className="p-2 text-base-content/50 hover:text-base-content/70 rounded-md hover:bg-base-200"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-base-content">
                {application.organisationName}
              </h1>
              <p className="text-base-content/70">{application.email}</p>
            </div>
          </div>
          <span
            className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${STATUS_COLORS[application.status]}`}
          >
            {STATUS_LABELS[application.status]}
          </span>
        </div>
      </div>

      <ErrorBanner error={error} onClose={clearError} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <div className="bg-base-100 shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-base-content mb-4">Contact Information</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-base-content/70">Name</dt>
                <dd className="text-sm text-base-content">
                  {[application.firstName, application.lastName].filter(Boolean).join(' ') || 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-base-content/70">Email</dt>
                <dd className="text-sm text-base-content">{application.email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-base-content/70">Phone</dt>
                <dd className="text-sm text-base-content">{application.phoneNumber || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-base-content/70">Job Role</dt>
                <dd className="text-sm text-base-content">{application.jobRole || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-base-content/70">Department</dt>
                <dd className="text-sm text-base-content">{application.department || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-base-content/70">Company / Freelance</dt>
                <dd className="text-sm text-base-content">
                  {application.companyFreelance || 'N/A'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Plan Details */}
          <div className="bg-base-100 shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-base-content mb-4">Plan Details</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-base-content/70">Form Type</dt>
                <dd className="text-sm text-base-content">
                  {application.formType === 'free' ? 'Free Tier' : 'Flexible Pricing'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-base-content/70">Form Source</dt>
                <dd className="text-sm text-base-content">{application.formSource || 'N/A'}</dd>
              </div>
              {application.requestedUsers !== undefined && (
                <div>
                  <dt className="text-sm font-medium text-base-content/70">Requested Users</dt>
                  <dd className="text-sm text-base-content">{application.requestedUsers}</dd>
                </div>
              )}
              {application.requestedPrograms !== undefined && (
                <div>
                  <dt className="text-sm font-medium text-base-content/70">Requested Programs</dt>
                  <dd className="text-sm text-base-content">{application.requestedPrograms}</dd>
                </div>
              )}
              {application.billingPeriod && (
                <div>
                  <dt className="text-sm font-medium text-base-content/70">Billing Period</dt>
                  <dd className="text-sm text-base-content">{application.billingPeriod}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-base-content/70">Currency</dt>
                <dd className="text-sm text-base-content">{application.currency}</dd>
              </div>
              {application.planPrice !== undefined && (
                <div>
                  <dt className="text-sm font-medium text-base-content/70">Plan Price</dt>
                  <dd className="text-sm text-base-content">
                    {application.planPrice} / {application.planFrequency || 'month'}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Marketing Context */}
          {(application.heardAbout || application.useIntent || application.message) && (
            <div className="bg-base-100 shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-base-content mb-4">Additional Context</h2>
              <dl className="space-y-4">
                {application.heardAbout && (
                  <div>
                    <dt className="text-sm font-medium text-base-content/70">
                      How did they hear about us?
                    </dt>
                    <dd className="text-sm text-base-content">{application.heardAbout}</dd>
                  </div>
                )}
                {application.useIntent && (
                  <div>
                    <dt className="text-sm font-medium text-base-content/70">Use Intent</dt>
                    <dd className="text-sm text-base-content">{application.useIntent}</dd>
                  </div>
                )}
                {application.message && (
                  <div>
                    <dt className="text-sm font-medium text-base-content/70">Message</dt>
                    <dd className="text-sm text-base-content whitespace-pre-wrap">
                      {application.message}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Notes */}
          <div className="bg-base-100 shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-base-content mb-4">Admin Notes</h2>
            {application.adminNotes.length === 0 ? (
              <p className="text-sm text-base-content/70 mb-4">No notes yet</p>
            ) : (
              <div className="space-y-3 mb-4">
                {application.adminNotes.map((note, idx) => (
                  <div key={idx} className="border-l-2 border-blue-300 pl-3 py-1">
                    <p className="text-sm text-base-content">{note.note}</p>
                    <p className="text-xs text-base-content/70">
                      {note.adminEmail} &mdash; {new Date(note.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={handleAddNote} className="flex space-x-2">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="flex-1 px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                disabled={addingNote || !newNote.trim()}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/80 disabled:bg-base-content/40 disabled:cursor-not-allowed"
              >
                {addingNote ? 'Adding...' : 'Add Note'}
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Transitions */}
          <div className="bg-base-100 shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-base-content mb-4">Actions</h2>
            {allowedTransitions.length === 0 ? (
              <p className="text-sm text-base-content/70">
                No actions available &mdash; this application is in a terminal state.
              </p>
            ) : (
              <div className="space-y-2">
                {allowedTransitions.map((target) => (
                  <button
                    key={target}
                    onClick={() => setTransitionTarget(target)}
                    className={`w-full px-4 py-2 text-sm font-medium rounded-md ${
                      target === 'approved' || target === 'payment_received'
                        ? 'btn btn-success'
                        : target === 'rejected'
                          ? 'btn btn-error'
                          : 'btn btn-neutral'
                    }`}
                  >
                    {STATUS_LABELS[target]}
                  </button>
                ))}
              </div>
            )}

            {/* Convert to Organisation */}
            {canConvert && (
              <div className="mt-4 pt-4 border-t border-base-300">
                <button
                  onClick={() => setShowConvert(!showConvert)}
                  className="w-full px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Convert to Organisation
                </button>
                {showConvert && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-base-content mb-1">
                        Plan
                      </label>
                      <select
                        value={convertPlanId}
                        onChange={(e) => setConvertPlanId(e.target.value)}
                        className="w-full px-3 py-2 border border-base-300 rounded-md text-sm"
                      >
                        <option value="">Select a plan</option>
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-base-content mb-1">
                        Billing Method
                      </label>
                      <select
                        value={convertBillingMethod}
                        onChange={(e) =>
                          setConvertBillingMethod(e.target.value as 'automated' | 'invoice')
                        }
                        className="w-full px-3 py-2 border border-base-300 rounded-md text-sm"
                      >
                        <option value="automated">Automated</option>
                        <option value="invoice">Invoice</option>
                      </select>
                    </div>
                    <button
                      onClick={handleConvert}
                      disabled={converting || !convertPlanId}
                      className="w-full px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-base-content/40 disabled:cursor-not-allowed"
                    >
                      {converting ? 'Converting...' : 'Confirm Conversion'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Converted org link */}
            {application.convertedOrganisationId && (
              <div className="mt-4 pt-4 border-t border-base-300">
                <p className="text-sm text-base-content/70 mb-2">Converted Organisation</p>
                <button
                  onClick={() =>
                    navigate(`/organisations?org=${application.convertedOrganisationId}`)
                  }
                  className="text-sm text-primary hover:text-primary/80 font-medium"
                >
                  View Organisation
                </button>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="bg-base-100 shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-base-content mb-4">Metadata</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-base-content/70">Application ID</dt>
                <dd className="text-xs text-base-content font-mono break-all">{application.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-base-content/70">Created</dt>
                <dd className="text-sm text-base-content">
                  {new Date(application.createdAt).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-base-content/70">Updated</dt>
                <dd className="text-sm text-base-content">
                  {new Date(application.updatedAt).toLocaleString()}
                </dd>
              </div>
              {application.reviewedBy && (
                <div>
                  <dt className="text-sm font-medium text-base-content/70">Reviewed By</dt>
                  <dd className="text-sm text-base-content">{application.reviewedBy}</dd>
                </div>
              )}
              {application.reviewedAt && (
                <div>
                  <dt className="text-sm font-medium text-base-content/70">Reviewed At</dt>
                  <dd className="text-sm text-base-content">
                    {new Date(application.reviewedAt).toLocaleString()}
                  </dd>
                </div>
              )}
              {application.rejectionReason && (
                <div>
                  <dt className="text-sm font-medium text-base-content/70">Rejection Reason</dt>
                  <dd className="text-sm text-red-600">{application.rejectionReason}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Status Transition Dialog */}
      <ConfirmDialog
        isOpen={!!transitionTarget}
        title={`Change Status to ${transitionTarget ? STATUS_LABELS[transitionTarget] : ''}`}
        message={
          transitionTarget === 'rejected'
            ? `Are you sure you want to reject this application? You can provide a reason below.`
            : `Are you sure you want to change the status to "${transitionTarget ? STATUS_LABELS[transitionTarget] : ''}"?`
        }
        confirmLabel={transitionLoading ? 'Processing...' : 'Confirm'}
        cancelLabel="Cancel"
        onConfirm={handleStatusTransition}
        onCancel={() => {
          setTransitionTarget(null);
          setRejectionReason('');
        }}
        confirmDisabled={transitionLoading}
      />

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default ApplicationDetail;
